/**
 * Utility functions for handling avatar URLs consistently across the app
 */

/**
 * Ensures an avatar URL is properly formatted as a full URL
 * This handles both full URLs and storage paths
 *
 * @param avatarUrl The avatar URL or storage path from Supabase
 * @returns A properly formatted full URL to the avatar image
 */
export function resolveAvatarStorageUrl(avatarUrl: string | null): string | null {
	if (!avatarUrl) {
		return null;
	}

	// Special case: If the URL contains "data:image" but is not at the start,
	// it's likely a malformed URL with a data URL embedded inside it
	if (avatarUrl.includes("data:image") && !avatarUrl.startsWith("data:")) {
		// Extract the data URL part
		const dataMatch = avatarUrl.match(
			/(data:image\/(jpeg|png|gif|webp);base64,[^"']+)/,
		);
		if (dataMatch?.[1]) {
			console.log("Extracted embedded data URL from malformed avatar URL");
			return dataMatch[1];
		}
	}

	// If it's a data URL, return it directly
	if (avatarUrl.startsWith("data:")) {
		return avatarUrl;
	}

	// If already a full URL with http/https, return as is
	if (avatarUrl.startsWith("http")) {
		return avatarUrl;
	}

	// If it's a storage path, convert to full URL
	// Make sure we have the Supabase URL
	const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
	if (!supabaseUrl) {
		console.error("NEXT_PUBLIC_SUPABASE_URL environment variable is missing");
		return null;
	}

	// Construct the full URL
	return `${supabaseUrl}/storage/v1/object/public/avatars/${avatarUrl}`;
}

/**
 * Extracts the initial from a user's username or name for avatar fallbacks
 * Prioritizes username over display name
 *
 * @param username The user's username (preferred)
 * @param displayName The user's display name (fallback)
 * @returns Single character representing the user's initial
 */
export function getUserInitials(
	username?: string | null,
	displayName?: string | null,
): string {
	// Prioritize username if available
	const name = username || displayName;

	if (!name) return "??";

	// Get first character of the name
	const trimmed = name.trim();
	if (trimmed.length === 0) return "??";

	return trimmed.charAt(0).toUpperCase();
}
